import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Map, FileText, Building, Plane, Users, UsersRound, Briefcase, ArrowRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { services } from '../data/mock';

const iconMap = {
  Map,
  FileText,
  Building,
  Plane,
  Users,
  UsersRound,
  Briefcase
};

const ServicesOverview = () => {
  const navigate = useNavigate();

  const handleLearnMore = (serviceId) => {
    // Navigate to contact page with service pre-selected
    navigate('/contact', { state: { preSelectedService: serviceId } });
  };

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Comprehensive Travel
            <span className="block bg-gradient-to-r from-[#FE4B79] to-[#6C30ED] bg-clip-text text-transparent">
              Services & Solutions
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            From initial planning to your safe return, we handle every aspect of your journey 
            with precision and care.
          </p>
        </div>

        {/* Services grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {services.map((service, index) => {
            const IconComponent = iconMap[service.icon];
            return (
              <Card 
                key={service.id}
                className="group hover:shadow-lg transition-all duration-300 border-0 shadow-md hover:-translate-y-1 bg-gradient-to-br from-white to-gray-50/50"
              >
                <CardHeader className="pb-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#6C30ED]/10 to-[#4E8EFF]/10 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <IconComponent size={28} className="text-[#6C30ED]" />
                  </div>
                  <CardTitle className="text-xl font-bold text-gray-900 group-hover:text-[#6C30ED] transition-colors">
                    {service.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 leading-relaxed mb-4">
                    {service.description}
                  </p>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => handleLearnMore(service.id)}
                    className="text-[#6C30ED] hover:bg-[#6C30ED]/10 p-0 h-auto font-semibold group/btn"
                  >
                    Learn More
                    <ArrowRight size={14} className="ml-2 group-hover/btn:translate-x-1 transition-transform" />
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-[#6C30ED]/5 via-[#4E8EFF]/5 to-[#00DF98]/5 rounded-3xl p-8 md:p-12 text-center border border-[#6C30ED]/10">
          <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
            Need Something Specific?
          </h3>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Our travel experts are standing by to create a completely customized solution 
            tailored to your unique requirements and preferences.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              className="bg-gradient-to-r from-[#6C30ED] to-[#4E8EFF] hover:from-[#5c28d4] hover:to-[#3d7eff] text-white font-semibold px-8 py-4 rounded-full shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300"
            >
              Get Custom Quote
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="border-2 border-[#6C30ED] text-[#6C30ED] hover:bg-[#6C30ED] hover:text-white font-semibold px-8 py-4 rounded-full transition-all duration-300"
            >
              View All Services
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesOverview;